import React, { useState, useCallback, useEffect } from 'react'
import { Modal, Button, Slider, Row, Col, Space } from 'antd'
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons'
import { postQuitarFondoPhotoroom } from 'src/services/axiomaService'

const DEFAULT_BRIGHTNESS = 100
const DEFAULT_CONTRAST = 100
const DEFAULT_SATURATION = 100

/**
 * Aplica filtros (brillo, contraste, saturación) a una imagen y devuelve Blob
 */
const aplicarFiltrosYExportar = (urlImagen, brightness, contrast, saturation) =>
  new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.width
      canvas.height = img.height
      const ctx = canvas.getContext('2d')
      const filter = `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%)`
      ctx.filter = filter
      ctx.drawImage(img, 0, 0)
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('toBlob failed'))),
        'image/png',
        0.95
      )
    }
    img.onerror = () => reject(new Error('Error al cargar imagen'))
    img.src = urlImagen
  })

/**
 * Modal para mostrar la imagen capturada: quitar fondo, ajustar brillo/contraste/saturación y guardar en catálogo
 */
const ModalMedidasCanvas = ({
  visible,
  onClose,
  onGuardarFoto,
  imagenFirmadaUrl,
  imagenFirmadaBlob,
  onError,
}) => {
  const [imagenSinFondoUrl, setImagenSinFondoUrl] = useState(null)
  // Blob de la imagen sin fondo; se envía directo a Photoroom porque el CSP de prod bloquea fetch a URLs blob:
  const [imagenSinFondoBlob, setImagenSinFondoBlob] = useState(null)
  const [brightness, setBrightness] = useState(DEFAULT_BRIGHTNESS)
  const [contrast, setContrast] = useState(DEFAULT_CONTRAST)
  const [saturation, setSaturation] = useState(DEFAULT_SATURATION)
  const [loadingQuitarFondo, setLoadingQuitarFondo] = useState(false)
  const [loadingGuardar, setLoadingGuardar] = useState(false)
  const [errorQuitarFondo, setErrorQuitarFondo] = useState(null)

  // Al abrir el modal o al cambiar la imagen (nueva foto), restaurar todo el estado para no mostrar la imagen anterior ni el "quitar fondo" previo
  useEffect(() => {
    if (visible && imagenFirmadaUrl) {
      if (imagenSinFondoUrl) {
        URL.revokeObjectURL(imagenSinFondoUrl)
      }
      setImagenSinFondoUrl(null)
      setImagenSinFondoBlob(null)
      setBrightness(DEFAULT_BRIGHTNESS)
      setContrast(DEFAULT_CONTRAST)
      setSaturation(DEFAULT_SATURATION)
      setErrorQuitarFondo(null)
    }
  }, [visible, imagenFirmadaUrl])

  const imagenActual = imagenSinFondoUrl || imagenFirmadaUrl
  const tieneFondoQuitado = Boolean(imagenSinFondoUrl)
  const filterStyle = {
    filter: `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%)`,
  }

  const handleQuitarFondo = useCallback(async () => {
    const fuenteParaProcesar =
      imagenSinFondoBlob || imagenFirmadaBlob || imagenSinFondoUrl || imagenFirmadaUrl
    if (!fuenteParaProcesar) return
    setLoadingQuitarFondo(true)
    setErrorQuitarFondo(null)
    try {
      const blobBlanco = await postQuitarFondoPhotoroom(fuenteParaProcesar)
      const nuevaUrl = URL.createObjectURL(blobBlanco)
      if (imagenSinFondoUrl) URL.revokeObjectURL(imagenSinFondoUrl)
      setImagenSinFondoUrl(nuevaUrl)
      setImagenSinFondoBlob(blobBlanco)
    } catch (err) {
      const msg = err?.message || 'Error al quitar el fondo con Photoroom'
      setErrorQuitarFondo(msg)
      if (typeof onError === 'function') onError(msg)
    } finally {
      setLoadingQuitarFondo(false)
    }
  }, [imagenFirmadaUrl, imagenFirmadaBlob, imagenSinFondoUrl, imagenSinFondoBlob, onError])

  const handleRestaurarOriginal = useCallback(() => {
    if (imagenSinFondoUrl) {
      URL.revokeObjectURL(imagenSinFondoUrl)
      setImagenSinFondoUrl(null)
    }
    setImagenSinFondoBlob(null)
    setBrightness(DEFAULT_BRIGHTNESS)
    setContrast(DEFAULT_CONTRAST)
    setSaturation(DEFAULT_SATURATION)
  }, [imagenSinFondoUrl])

  const handleGuardar = useCallback(async () => {
    if (!imagenActual) return
    setLoadingGuardar(true)
    try {
      const blob = await aplicarFiltrosYExportar(
        imagenActual,
        brightness,
        contrast,
        saturation
      )
      await onGuardarFoto(blob)
    } catch (err) {
      if (typeof onError === 'function') {
        onError(err?.message || 'Error al exportar la imagen')
      }
    } finally {
      setLoadingGuardar(false)
    }
  }, [imagenActual, brightness, contrast, saturation, onGuardarFoto])

  const handleClose = useCallback(() => {
    if (imagenSinFondoUrl) URL.revokeObjectURL(imagenSinFondoUrl)
    setImagenSinFondoUrl(null)
    setImagenSinFondoBlob(null)
    setBrightness(DEFAULT_BRIGHTNESS)
    setContrast(DEFAULT_CONTRAST)
    setSaturation(DEFAULT_SATURATION)
    onClose()
  }, [imagenSinFondoUrl, onClose])

  return (
    <Modal
      title="Imagen Capturada"
      open={visible}
      maskClosable={false}
      onCancel={handleClose}
      footer={[
        <Button key="cerrar" onClick={handleClose}>
          Cerrar
        </Button>,
        <Button
          key="guardar-foto"
          type="primary"
          loading={loadingGuardar}
          onClick={handleGuardar}
          icon={<UploadOutlined />}
        >
          Guardar foto para catálogo
        </Button>,
      ]}
      width={900}
      centered
    >
      {imagenFirmadaUrl && (
        <Row gutter={24}>
          <Col span={16}>
            <div
              style={{
                textAlign: 'center',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '380px',
                backgroundColor: '#fafafa',
                borderRadius: 8,
                padding: 16,
              }}
            >
              <img
                src={imagenActual}
                alt="Imagen capturada"
                style={{
                  maxWidth: '100%',
                  maxHeight: '420px',
                  height: 'auto',
                  width: 'auto',
                  objectFit: 'contain',
                  borderRadius: 8,
                  border: '1px solid #d9d9d9',
                  ...filterStyle,
                }}
              />
            </div>
          </Col>
          <Col span={8}>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Button
                type="default"
                block
                loading={loadingQuitarFondo}
                onClick={handleQuitarFondo}
                icon={<DeleteOutlined />}
              >
                Quitar fondo (fondo blanco)
              </Button>
              {errorQuitarFondo && (
                <div style={{ fontSize: 12, color: '#ff4d4f' }}>
                  {errorQuitarFondo}
                </div>
              )}
              {tieneFondoQuitado && (
                <Button type="default" block onClick={handleRestaurarOriginal}>
                  Restaurar imagen original
                </Button>
              )}
              <div>
                <div style={{ marginBottom: 4, fontWeight: 500 }}>Brillo</div>
                <Slider
                  min={50}
                  max={150}
                  value={brightness}
                  onChange={setBrightness}
                  tooltip={{ formatter: (v) => `${v}%` }}
                />
              </div>
              <div>
                <div style={{ marginBottom: 4, fontWeight: 500 }}>Contraste</div>
                <Slider
                  min={50}
                  max={150}
                  value={contrast}
                  onChange={setContrast}
                  tooltip={{ formatter: (v) => `${v}%` }}
                />
              </div>
              <div>
                <div style={{ marginBottom: 4, fontWeight: 500 }}>Saturación (colores)</div>
                <Slider
                  min={0}
                  max={200}
                  value={saturation}
                  onChange={setSaturation}
                  tooltip={{ formatter: (v) => `${v}%` }}
                />
              </div>
            </Space>
          </Col>
        </Row>
      )}
    </Modal>
  )
}

export default ModalMedidasCanvas
